import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  BookmarkIcon, 
  BookOpenIcon, 
  BeakerIcon, 
  RocketLaunchIcon,
  TrashIcon 
} from '@heroicons/react/24/solid';
import api from '../services/api';

export default function Bookmarks() {
  const [bookmarks, setBookmarks] = useState({ Module: [], Lab: [], Capstone: [] });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBookmarks();
  }, []);

  const fetchBookmarks = async () => {
    try {
      setLoading(true);
      const response = await api.get('/bookmarks');
      setBookmarks(response.data.bookmarks);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Failed to fetch bookmarks:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeBookmark = async (bookmarkId) => {
    try {
      await api.delete(`/bookmarks/${bookmarkId}`);
      fetchBookmarks(); // Refresh list
    } catch (error) {
      console.error('Failed to remove bookmark:', error);
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'Module':
        return <BookOpenIcon className="h-5 w-5 text-blue-600" />;
      case 'Lab':
        return <BeakerIcon className="h-5 w-5 text-purple-600" />;
      case 'Capstone':
        return <RocketLaunchIcon className="h-5 w-5 text-green-600" />;
      default:
        return <BookmarkIcon className="h-5 w-5 text-gray-600" />;
    }
  };

  const getColor = (type) => {
    switch (type) {
      case 'Module':
        return 'border-blue-200 hover:border-blue-400 bg-blue-50';
      case 'Lab':
        return 'border-purple-200 hover:border-purple-400 bg-purple-50';
      case 'Capstone':
        return 'border-green-200 hover:border-green-400 bg-green-50';
      default:
        return 'border-gray-200 hover:border-gray-400 bg-gray-50';
    }
  };

  const getLink = (type, id) => {
    const base = type.toLowerCase();
    return `/${base}s/${id}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cte-blue"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center mb-2">
          <BookmarkIcon className="h-8 w-8 text-yellow-500 mr-3" />
          <h1 className="text-3xl font-bold text-cte-black">My Bookmarks</h1>
        </div>
        <p className="text-gray-600">
          {total === 0 
            ? "You haven't bookmarked any content yet." 
            : `${total} ${total === 1 ? 'item' : 'items'} saved for later`
          }
        </p>
      </div>

      {total === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <BookmarkIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            No Bookmarks Yet
          </h3>
          <p className="text-gray-600 mb-4">
            Click the bookmark icon on any module, lab, or capstone to save it here.
          </p>
          <Link 
            to="/dashboard" 
            className="inline-flex items-center px-4 py-2 bg-cte-blue text-white rounded-lg hover:bg-cte-blue-dark transition-colors"
          >
            Browse Content →
          </Link>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Modules */}
          {bookmarks.Module && bookmarks.Module.length > 0 && (
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <BookOpenIcon className="h-6 w-6 text-blue-600 mr-2" />
                Modules ({bookmarks.Module.length})
              </h2>
              <div className="space-y-3">
                {bookmarks.Module.map((bookmark) => (
                  <div
                    key={bookmark.id}
                    className={`flex items-center justify-between p-4 rounded-lg border-2 transition-all ${getColor('Module')}`}
                  >
                    <Link
                      to={getLink('Module', bookmark.content_id)}
                      className="flex-1 flex items-start space-x-4 hover:opacity-80"
                    >
                      {getIcon('Module')}
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="text-xs font-semibold text-blue-600 uppercase">
                            {bookmark.code}
                          </span>
                        </div>
                        <h3 className="font-semibold text-gray-900">{bookmark.title}</h3>
                        {bookmark.description && (
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                            {bookmark.description}
                          </p>
                        )}
                      </div>
                    </Link>
                    <button
                      onClick={() => removeBookmark(bookmark.id)}
                      className="ml-4 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Remove bookmark"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Labs */}
          {bookmarks.Lab && bookmarks.Lab.length > 0 && (
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <BeakerIcon className="h-6 w-6 text-purple-600 mr-2" />
                Labs ({bookmarks.Lab.length})
              </h2>
              <div className="space-y-3">
                {bookmarks.Lab.map((bookmark) => (
                  <div
                    key={bookmark.id}
                    className={`flex items-center justify-between p-4 rounded-lg border-2 transition-all ${getColor('Lab')}`}
                  >
                    <Link
                      to={getLink('Lab', bookmark.content_id)}
                      className="flex-1 flex items-start space-x-4 hover:opacity-80"
                    >
                      {getIcon('Lab')}
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="text-xs font-semibold text-purple-600 uppercase">
                            {bookmark.code}
                          </span>
                        </div>
                        <h3 className="font-semibold text-gray-900">{bookmark.title}</h3>
                        {bookmark.description && (
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                            {bookmark.description}
                          </p>
                        )}
                      </div>
                    </Link>
                    <button
                      onClick={() => removeBookmark(bookmark.id)}
                      className="ml-4 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Remove bookmark"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Capstones */}
          {bookmarks.Capstone && bookmarks.Capstone.length > 0 && (
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <RocketLaunchIcon className="h-6 w-6 text-green-600 mr-2" />
                Capstones ({bookmarks.Capstone.length})
              </h2>
              <div className="space-y-3">
                {bookmarks.Capstone.map((bookmark) => (
                  <div
                    key={bookmark.id}
                    className={`flex items-center justify-between p-4 rounded-lg border-2 transition-all ${getColor('Capstone')}`}
                  >
                    <Link
                      to={getLink('Capstone', bookmark.content_id)}
                      className="flex-1 flex items-start space-x-4 hover:opacity-80"
                    >
                      {getIcon('Capstone')}
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="text-xs font-semibold text-green-600 uppercase">
                            {bookmark.code}
                          </span>
                        </div>
                        <h3 className="font-semibold text-gray-900">{bookmark.title}</h3>
                        {bookmark.description && (
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                            {bookmark.description}
                          </p>
                        )}
                      </div>
                    </Link>
                    <button
                      onClick={() => removeBookmark(bookmark.id)}
                      className="ml-4 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Remove bookmark"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
